import React, { useState } from 'react';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import Button from '@mui/material/Button';
import * as yup from 'yup';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import { CircularProgress } from '@mui/material';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import { updateEnquiry } from '../../../redux/actions/apiActions';
import { ENQUIRY_SERVICES, ENQUIRY_STATUSES } from './AddEnquiry';

const validationSchema = yup.object({
    name: yup.string().trim().required('Name is required.'),
    contactNumber: yup
        .string()
        .required('Contact number is required.')
        .matches(/^[0-9]{10}$/, 'Contact number must be exactly 10 digits.'),
    service: yup.string().required('Service is required.'),
    status: yup.string().required('Status is required.'),
    followUpDate: yup
        .string()
        .nullable()
        .test('not-past', 'Follow-up date cannot be in the past.', (val) => {
            if (!val) return true;
            return val >= new Date().toISOString().split('T')[0];
        }),
    notes: yup.string().trim(),
});

const EditEnquiry = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const { state } = useLocation();
    const enquiryData = state?.EnquiryData;

    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
    const [openSidebar, setOpenSidebar] = useState(false);

    const handleSidebarOpen = () => setOpenSidebar(true);
    const handleSidebarClose = () => setOpenSidebar(false);
    const open = isMd ? false : openSidebar;

    const handleOnSubmit = async (values) => {
        dispatch(setGlobalLoader(true));
        try {
            const payload = { ...values };
            if (!payload.followUpDate) delete payload.followUpDate;
            await updateEnquiry(params.id, payload);
            toast.success('Enquiry updated successfully');
            navigate('/enquiry');
            dispatch(setGlobalLoader(false));
        } catch (error) {
            dispatch(setGlobalLoader(false));
            console.log('update enquiry error', error);
            toast.error(error?.response?.data?.message ?? 'Error updating enquiry');
        }
    };

    if (!enquiryData) {
        return (
            <Box>
                <AppBar
                    position={'fixed'}
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                    elevation={0}
                >
                    <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                        <Topbar onSidebarOpen={handleSidebarOpen} />
                    </Container>
                </AppBar>
                <Sidebar onClose={handleSidebarClose} open={open} variant={isMd ? 'permanent' : 'temporary'} />
                <main>
                    <Box height={{ xs: 94, sm: 102, md: 107 }} />
                    <Box overflow="hidden" marginLeft={{ md: '270px' }} p={3}>
                        <Typography variant="h6">No enquiry data found</Typography>
                        <Button variant="contained" onClick={() => navigate('/enquiry')} sx={{ mt: 2 }}>
                            Go Back to Enquiries
                        </Button>
                    </Box>
                </main>
            </Box>
        );
    }

    const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toISOString().split('T')[0];
        } catch {
            return '';
        }
    };

    return (
        <Box>
            <AppBar
                position={'fixed'}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={handleSidebarOpen} />
                </Container>
            </AppBar>
            <Sidebar onClose={handleSidebarClose} open={open} variant={isMd ? 'permanent' : 'temporary'} />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <br />
                <br />
                <Box overflow="hidden" marginLeft={{ md: '270px' }}>
                    <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                        Edit Enquiry
                    </Typography>

                    <Formik
                        initialValues={{
                            name: enquiryData?.name || '',
                            contactNumber: enquiryData?.contactNumber || '',
                            service: enquiryData?.service || '',
                            status: enquiryData?.status || 'new',
                            followUpDate: formatDateForInput(enquiryData?.followUpDate),
                            notes: enquiryData?.notes || '',
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values) => handleOnSubmit(values)}
                        enableReinitialize
                    >
                        {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Name *
                                        </Typography>
                                        <TextField
                                            label="Full Name"
                                            variant="outlined"
                                            name="name"
                                            fullWidth
                                            value={values.name}
                                            onChange={(e) => setFieldValue('name', e.target.value)}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Contact Number *
                                        </Typography>
                                        <TextField
                                            label="Contact Number"
                                            variant="outlined"
                                            name="contactNumber"
                                            fullWidth
                                            inputProps={{ maxLength: 10 }}
                                            value={values.contactNumber}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                setFieldValue('contactNumber', val);
                                            }}
                                            error={touched.contactNumber && Boolean(errors.contactNumber)}
                                            helperText={touched.contactNumber && errors.contactNumber}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Service *
                                        </Typography>
                                        <Select
                                            styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                                            placeholder="Select Service"
                                            value={ENQUIRY_SERVICES.find((opt) => opt.value === values.service) || null}
                                            options={ENQUIRY_SERVICES}
                                            onChange={(e) => setFieldValue('service', e.value)}
                                        />
                                        {touched.service && errors.service && (
                                            <Typography variant="caption" color="error">
                                                {errors.service}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Status *
                                        </Typography>
                                        <Select
                                            styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                                            placeholder="Select Status"
                                            value={ENQUIRY_STATUSES.find((opt) => opt.value === values.status) || null}
                                            options={ENQUIRY_STATUSES}
                                            onChange={(e) => setFieldValue('status', e.value)}
                                        />
                                        {touched.status && errors.status && (
                                            <Typography variant="caption" color="error">
                                                {errors.status}
                                            </Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Follow Up Date
                                        </Typography>
                                        <TextField
                                            label="Follow Up Date"
                                            variant="outlined"
                                            name="followUpDate"
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                            value={values.followUpDate}
                                            onChange={(e) => setFieldValue('followUpDate', e.target.value)}
                                            error={touched.followUpDate && Boolean(errors.followUpDate)}
                                            helperText={touched.followUpDate && errors.followUpDate}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Notes
                                        </Typography>
                                        <TextField
                                            label="Notes"
                                            variant="outlined"
                                            name="notes"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={values.notes}
                                            onChange={(e) => setFieldValue('notes', e.target.value)}
                                            error={touched.notes && Boolean(errors.notes)}
                                            helperText={touched.notes && errors.notes}
                                        />
                                    </Grid>
                                </Grid>
                                <br />
                                <Button
                                    component={'button'}
                                    variant={'contained'}
                                    fontWeight={700}
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? <CircularProgress size={24} /> : 'Update'}
                                </Button>
                            </form>
                        )}
                    </Formik>
                </Box>
            </main>
        </Box>
    );
};

export default EditEnquiry;
